// Rep detail page (owner request 2026-10-14). Demo-mode / FakeRest mirror of private.rep_counts_at, private.rep_kpis,
// private.rep_stats_at and private.rep_conversations_at (supabase/schemas/48_gavion_rep_stats.sql; the live
// rep_conversations_at is in 52_gavion_small_fixes.sql), plus the pure view helpers of /rapports/vendeurs/:salesId. The demo always uses America/Toronto (the SQL default zone).
// A conversation is exactly what the leaderboard counts: CONTACT_OUTCOMES (DIAL_OUTCOMES.reachedHuman).
import { leaderboardRange } from "../../reports/data/localLeaderboard";
import type {
  CallHoursDays,
  ConversationOutcome,
  LeaderboardPeriod,
  RepConversation,
  RepConversationsPage,
  RepCounts,
  RepTrendDay,
  RepKpis,
  RepSegmentRow,
  RepStatsReport,
} from "../../reports/types";
import { DIAL_OUTCOMES } from "../dialer/outcomes";
import { daysInclusive, periodBounds, previousPeriod } from "../funnel/period";
import { CONTACT_OUTCOMES } from "../funnel/prospectingFunnel";
import { ratio, round } from "../funnel/stats";
import { addDays, torontoDate } from "../time/toronto";

export type RepStatsCall = {
  id: number;
  company_id: number | null;
  sales_id: number;
  direction: "outbound" | "inbound";
  dialed_at: string;
  outcome: string | null;
  deal_id?: number | null;
  contact_id?: number | null;
  callback_at?: string | null;
  not_interested_reason?: string | null;
  note?: string | null;
  demo?: { at?: string | null } | null;
  recording_path?: string | null;
  billsec_s?: number | null;
  dial_session_id?: number | null;
  claimed_at?: string | null;
  outcome_at?: string | null;
};
export type RepStatsDeal = {
  id: number;
  name?: string | null;
  company_id?: number | null;
  stage: string;
  amount: number | null;
  created_at: string;
  won_at?: string | null;
  archived_at?: string | null;
  sales_id?: number | null;
  source?: string | null;
  demo_at?: string | null;
};
export type RepStatsSession = {
  id?: number;
  sales_id: number;
  started_at: string;
  ended_at: string | null;
};
export type RepStatsSale = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  administrator?: boolean | null;
  disabled?: boolean | null;
};
export type RepStatsCompany = {
  id: number;
  name?: string | null;
  trade?: string | null;
  region?: string | null;
};
export type RepStatsContact = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
};
export type RepStatsMeetingNote = {
  id: number;
  title?: string | null;
  started_at?: string | null;
  share_url?: string | null;
  deal_id?: number | null;
  company_id?: number | null;
  match_status?: string | null;
  /** GAVION granola-calls: the call the note was taken on (meeting_notes.call_id). */
  call_id?: number | null;
};

/** Window (± minutes around callback_at) in which a callback counts as kept. SQL: interval '30 minutes'. */
export const CALLBACK_WINDOW_MINUTES = 30;
const CALLBACK_WINDOW_MS = CALLBACK_WINDOW_MINUTES * 60_000;

const isDial = (c: RepStatsCall) =>
  c.direction === "outbound" && c.outcome !== "skipped";
const isConversation = (outcome: string | null | undefined) =>
  CONTACT_OUTCOMES.has(outcome ?? "");
const isWon = (d: RepStatsDeal) => d.stage === "gagne" && !d.archived_at;
const fullName = (s: {
  first_name?: string | null;
  last_name?: string | null;
}) => `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim();
const isAdminSale = (s: RepStatsSale) =>
  s.role === "admin" || s.administrator === true;

const zeroCounts = (): RepCounts => ({
  dials: 0,
  conversations: 0,
  demos: 0,
  deals_won: 0,
  revenue_won: 0,
  session_seconds: 0,
  demos_closed: 0,
  callbacks_due: 0,
  callbacks_kept: 0,
  first_conversation_leads: 0,
  first_conversation_attempts: 0,
});

const LIVE_SESSION_MS = 15 * 60_000;
const SESSION_CAP_MS = 12 * 3_600_000;
/** Latest activity (claimed_at / dialed_at / outcome_at) per dial session id. */
export const sessionActivity = (
  calls: readonly Pick<
    RepStatsCall,
    "dial_session_id" | "claimed_at" | "dialed_at" | "outcome_at"
  >[],
): Map<number, number> => {
  const out = new Map<number, number>();
  for (const c of calls) {
    if (c.dial_session_id == null) continue;
    const t = Math.max(
      ...[c.claimed_at, c.dialed_at, c.outcome_at]
        .filter((x): x is string => !!x)
        .map(Date.parse),
    );
    if (t > (out.get(c.dial_session_id) ?? -Infinity))
      out.set(c.dial_session_id, t);
  }
  return out;
};
/**
 * Mirror of private.dial_session_end (epoch ms): ended_at for an ended session; for an open one, its last activity
 * (start or latest call time), or now when that activity is within 15 min (a live session). Capped at 12 h.
 */
export const sessionEnd = (
  s: RepStatsSession,
  activity: ReadonlyMap<number, number>,
  now: number,
): number => {
  const start = Date.parse(s.started_at);
  let end: number;
  if (s.ended_at) end = Date.parse(s.ended_at);
  else {
    const last = Math.max(
      start,
      s.id != null ? (activity.get(s.id) ?? -Infinity) : -Infinity,
    );
    end = last >= now - LIVE_SESSION_MS ? Math.max(now, last) : last;
  }
  return Math.min(start + SESSION_CAP_MS, end);
};

/** Mirror of private.rep_counts_at: one entry per sales id with any activity in the period. */
export const computeRepCounts = (input: {
  calls: readonly RepStatsCall[];
  deals: readonly RepStatsDeal[];
  sessions: readonly RepStatsSession[];
  from: string;
  to: string;
  now: Date;
}): Map<number, RepCounts> => {
  const { start, end } = periodBounds(input.from, input.to);
  const now = input.now.getTime();
  const inPeriod = (iso: string | null | undefined) => {
    if (!iso) return false;
    const t = Date.parse(iso);
    return t >= start && t < end;
  };
  const out = new Map<number, RepCounts>();
  const row = (id: number) => {
    let r = out.get(id);
    if (!r) out.set(id, (r = zeroCounts()));
    return r;
  };
  const dealsById = new Map(input.deals.map((d) => [d.id, d]));

  const periodDials = input.calls.filter(
    (c) => isDial(c) && inPeriod(c.dialed_at),
  );
  for (const c of periodDials) {
    const r = row(c.sales_id);
    r.dials += 1;
    if (isConversation(c.outcome)) r.conversations += 1;
    if (c.outcome === "demo_booked") {
      r.demos += 1;
      const deal = c.deal_id != null ? dealsById.get(c.deal_id) : undefined;
      if (deal && isWon(deal)) r.demos_closed += 1;
    }
  }
  for (const d of input.deals) {
    if (!isWon(d) || d.sales_id == null || !inPeriod(d.won_at)) continue;
    const r = row(d.sales_id);
    r.deals_won += 1;
    r.revenue_won += d.amount ?? 0;
  }
  const activity = sessionActivity(input.calls);
  for (const s of input.sessions) {
    const sStart = Date.parse(s.started_at);
    const sEnd = sessionEnd(s, activity, now);
    if (!(sStart < end && sEnd > start)) continue;
    row(s.sales_id).session_seconds +=
      (Math.min(sEnd, end) - Math.max(sStart, start)) / 1000;
  }
  for (const c of input.calls) {
    if (
      c.direction !== "outbound" ||
      c.outcome !== "talked_callback" ||
      c.company_id == null ||
      !c.callback_at ||
      !inPeriod(c.callback_at)
    )
      continue;
    const due = Date.parse(c.callback_at);
    if (due + CALLBACK_WINDOW_MS > now) continue;
    const r = row(c.sales_id);
    r.callbacks_due += 1;
    const kept = input.calls.some(
      (k) =>
        k.company_id === c.company_id &&
        k.sales_id === c.sales_id &&
        k.id !== c.id &&
        isDial(k) &&
        Math.abs(Date.parse(k.dialed_at) - due) <= CALLBACK_WINDOW_MS,
    );
    if (kept) r.callbacks_kept += 1;
  }
  // first conversation ever per lead touched by a conversation in the period
  const convLeads = new Set(
    periodDials
      .filter((c) => c.company_id != null && isConversation(c.outcome))
      .map((c) => c.company_id as number),
  );
  const order = (a: RepStatsCall, b: RepStatsCall) =>
    Date.parse(a.dialed_at) - Date.parse(b.dialed_at) || a.id - b.id;
  for (const lead of convLeads) {
    const leadCalls = input.calls
      .filter((c) => c.company_id === lead && c.direction === "outbound")
      .sort(order);
    const first = leadCalls.find((c) => isConversation(c.outcome));
    if (!first || !inPeriod(first.dialed_at)) continue;
    const r = row(first.sales_id);
    r.first_conversation_leads += 1;
    r.first_conversation_attempts += leadCalls.filter(
      (k) => isDial(k) && order(k, first) <= 0,
    ).length;
  }
  return out;
};

/** Mirror of private.rep_kpis: counts (possibly averages) and their rates; a rate is null when its divisor is 0. */
export const kpisFromCounts = (p: Partial<RepCounts>): RepKpis => {
  const c = { ...zeroCounts(), ...p };
  return {
    dials: round(c.dials, 2),
    conversations: round(c.conversations, 2),
    demos: round(c.demos, 2),
    deals_won: round(c.deals_won, 2),
    revenue_won: round(c.revenue_won, 2),
    session_seconds: round(c.session_seconds, 0),
    demos_closed: round(c.demos_closed, 2),
    callbacks_due: round(c.callbacks_due, 2),
    callbacks_kept: round(c.callbacks_kept, 2),
    first_conversation_leads: round(c.first_conversation_leads, 2),
    first_conversation_attempts: round(c.first_conversation_attempts, 2),
    conversation_rate: ratio(c.conversations, c.dials, 4),
    demo_rate: ratio(c.demos, c.conversations, 4),
    close_rate: ratio(c.demos_closed, c.demos, 4),
    dials_per_session_hour:
      c.session_seconds >= 60
        ? round(c.dials / (c.session_seconds / 3600), 1)
        : null,
    attempts_per_first_conversation: ratio(
      c.first_conversation_attempts,
      c.first_conversation_leads,
      2,
    ),
    callbacks_kept_rate: ratio(c.callbacks_kept, c.callbacks_due, 4),
  };
};

const COUNT_KEYS = Object.keys(zeroCounts()) as (keyof RepCounts)[];

/** Mirror of private.rep_period_at: the rep's KPIs and the team averages (the leaderboard's members). */
const periodKpis = (
  input: Parameters<typeof computeRepCounts>[0] & {
    sales: readonly RepStatsSale[];
    salesId: number;
  },
) => {
  const counts = computeRepCounts(input);
  const team = input.sales.filter((s) => {
    if (s.disabled) return false;
    const c = counts.get(s.id);
    return !isAdminSale(s) || (c?.dials ?? 0) > 0 || (c?.deals_won ?? 0) > 0;
  });
  const n = team.length;
  const sums = zeroCounts();
  for (const s of team) {
    const c = counts.get(s.id);
    if (c) for (const k of COUNT_KEYS) sums[k] += c[k];
  }
  const avg = Object.fromEntries(
    COUNT_KEYS.map((k) => [k, n ? sums[k] / n : 0]),
  ) as RepCounts;
  return {
    kpis: kpisFromCounts(counts.get(input.salesId) ?? {}),
    team: n ? kpisFromCounts(avg) : null,
    team_size: n,
  };
};

const tally = <T>(items: readonly T[], key: (t: T) => string | null) => {
  const m = new Map<string | null, T[]>();
  for (const t of items) {
    const k = key(t);
    const list = m.get(k);
    if (list) list.push(t);
    else m.set(k, [t]);
  }
  return [...m.entries()];
};
const byC = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0); // SQL collate "C"
const byCountThenName =
  (name: (x: { n: number; k: string }) => string) =>
  (a: { n: number; k: string }, b: { n: number; k: string }) =>
    b.n - a.n || byC(name(a), name(b));
const segments = (
  rows: readonly { call: RepStatsCall; key: string | null }[],
): RepSegmentRow[] =>
  tally(rows, (r) => r.key)
    .map(([key, list]) => ({
      key,
      dials: list.length,
      conversations: list.filter((r) => isConversation(r.call.outcome)).length,
      demos: list.filter((r) => r.call.outcome === "demo_booked").length,
    }))
    .sort(
      (a, b) =>
        b.dials - a.dials ||
        (a.key === null ? 1 : b.key === null ? -1 : byC(a.key, b.key)),
    );

/** Mirror of private.rep_stats_at (America/Toronto). */
export const computeRepStats = (input: {
  calls: readonly RepStatsCall[];
  deals: readonly RepStatsDeal[];
  sessions: readonly RepStatsSession[];
  sales: readonly RepStatsSale[];
  companies: readonly RepStatsCompany[];
  salesId: number;
  from: string;
  to: string;
  now: Date;
}): RepStatsReport => {
  const prev = previousPeriod(input.from, input.to);
  const cur = periodKpis(input);
  const before = periodKpis({ ...input, from: prev.from, to: prev.to });
  const { start, end } = periodBounds(input.from, input.to);
  const companies = new Map(input.companies.map((c) => [c.id, c]));
  const mine = input.calls.filter((c) => {
    const t = Date.parse(c.dialed_at);
    return c.sales_id === input.salesId && isDial(c) && t >= start && t < end;
  });
  const sale = input.sales.find((s) => s.id === input.salesId);
  const company = (c: RepStatsCall) =>
    c.company_id != null ? companies.get(c.company_id) : undefined;
  return {
    sales_id: input.salesId,
    name: sale ? fullName(sale) : null,
    from: input.from,
    to: input.to,
    previous_from: prev.from,
    previous_to: prev.to,
    timezone: "America/Toronto",
    callback_window_minutes: CALLBACK_WINDOW_MINUTES,
    kpis: cur.kpis,
    previous: before.kpis,
    team: cur.team,
    team_size: cur.team_size,
    by_day: tally(mine, (c) => torontoDate(new Date(c.dialed_at)))
      .map(([day, list]) => ({
        day: day as string,
        dials: list.length,
        conversations: list.filter((c) => isConversation(c.outcome)).length,
        demos: list.filter((c) => c.outcome === "demo_booked").length,
      }))
      .sort((a, b) => byC(a.day, b.day)),
    by_outcome: tally(
      mine.filter((c) => c.outcome != null),
      (c) => c.outcome,
    )
      .map(([k, list]) => ({ k: k as string, n: list.length }))
      .sort(byCountThenName((x) => x.k))
      .map((x) => ({ outcome: x.k, count: x.n })),
    not_interested: tally(
      mine.filter((c) => c.outcome === "not_interested"),
      (c) => c.not_interested_reason?.trim() || "unknown",
    )
      .map(([k, list]) => ({ k: k as string, n: list.length }))
      .sort(byCountThenName((x) => x.k))
      .map((x) => ({ reason: x.k, count: x.n })),
    by_trade: segments(
      mine.map((call) => ({ call, key: company(call)?.trade ?? null })),
    ),
    by_region: segments(
      mine.map((call) => ({ call, key: company(call)?.region ?? null })),
    ),
  };
};

/** Mirror of private.rep_conversations_at (reached-human calls, newest first, one page). */
export const computeRepConversations = (input: {
  calls: readonly RepStatsCall[];
  companies: readonly RepStatsCompany[];
  contacts: readonly RepStatsContact[];
  deals: readonly RepStatsDeal[];
  meetingNotes: readonly RepStatsMeetingNote[];
  salesId: number;
  from: string;
  to: string;
  outcome: ConversationOutcome | null;
  limit: number;
  offset: number;
}): RepConversationsPage => {
  const { start, end } = periodBounds(input.from, input.to);
  const companies = new Map(input.companies.map((c) => [c.id, c]));
  const contacts = new Map(input.contacts.map((c) => [c.id, c]));
  const deals = new Map(input.deals.map((d) => [d.id, d]));
  const all = input.calls
    .filter((c) => {
      const t = Date.parse(c.dialed_at);
      return (
        c.sales_id === input.salesId &&
        c.direction === "outbound" &&
        isConversation(c.outcome) &&
        (input.outcome == null || c.outcome === input.outcome) &&
        t >= start &&
        t < end
      );
    })
    .sort(
      (a, b) =>
        Date.parse(b.dialed_at) - Date.parse(a.dialed_at) || b.id - a.id,
    );
  const SIXTY_DAYS = 60 * 86_400_000;
  const startedMs = (m: RepStatsMeetingNote) =>
    m.started_at ? Date.parse(m.started_at) : -Infinity;
  const note = (c: RepStatsCall) => {
    // GAVION small fixes 2: the note taken on this call (meeting_notes.call_id) first, latest one, ties by id
    const own = input.meetingNotes
      .filter((m) => m.match_status === "matched" && m.call_id === c.id)
      .sort((a, b) => startedMs(b) - startedMs(a) || b.id - a.id);
    return own.length ? noteRef(own[0]) : leadNote(c);
  };
  const noteRef = (m: RepStatsMeetingNote) => ({
    id: m.id,
    title: m.title ?? null,
    share_url: m.share_url ?? null,
    started_at: m.started_at ?? null,
  });
  /**
   * A call without a linked note: the deal's note, else the lead's latest note within 60 days after the call; never a
   * note linked to another call.
   */
  const leadNote = (c: RepStatsCall) => {
    const at = Date.parse(c.dialed_at);
    const candidates = input.meetingNotes.filter((m) => {
      if (m.match_status !== "matched") return false;
      // GAVION small fixes (review M2): never another call's note
      if (m.call_id != null && m.call_id !== c.id) return false;
      if (c.deal_id != null && m.deal_id === c.deal_id) return true;
      if (c.company_id == null || m.company_id !== c.company_id) return false;
      const t = m.started_at ? Date.parse(m.started_at) : NaN;
      return t >= at && t < at + SIXTY_DAYS;
    });
    candidates.sort(
      (a, b) =>
        Number((b.deal_id ?? null) === (c.deal_id ?? null)) -
          Number((a.deal_id ?? null) === (c.deal_id ?? null)) ||
        startedMs(b) - startedMs(a) ||
        b.id - a.id,
    );
    return candidates[0] ? noteRef(candidates[0]) : null;
  };
  const rows: RepConversation[] = all
    .slice(input.offset, input.offset + input.limit)
    .map((c) => {
      const deal = c.deal_id != null ? deals.get(c.deal_id) : undefined;
      const contact =
        c.contact_id != null ? contacts.get(c.contact_id) : undefined;
      return {
        id: c.id,
        dialed_at: c.dialed_at,
        outcome: c.outcome as ConversationOutcome,
        note: c.note ?? null,
        company_id: c.company_id,
        company_name:
          c.company_id != null
            ? (companies.get(c.company_id)?.name ?? null)
            : null,
        contact_id: c.contact_id ?? null,
        contact_name: contact ? fullName(contact) || null : null,
        callback_at: c.callback_at ?? null,
        demo_at: deal?.demo_at ?? c.demo?.at ?? null,
        not_interested_reason: c.not_interested_reason ?? null,
        deal_id: deal?.id ?? null,
        deal_name: deal?.name ?? null,
        deal_stage: deal?.stage ?? null,
        recording_path: c.recording_path ?? null,
        billsec_s: c.billsec_s ?? null,
        meeting_note: note(c),
      };
    });
  return { total: all.length, rows };
};

// ------------------------------------------------------------------ view helpers

export type RepPeriodPreset = LeaderboardPeriod | "last30" | "custom";
export const REP_PRESETS: readonly RepPeriodPreset[] = [
  "today",
  "week",
  "month",
  "last30",
  "custom",
];
/** The « Conversations » list: reached-human outcomes, 25 per page. */
export const CONVERSATION_OUTCOMES: readonly ConversationOutcome[] = [
  "talked_callback",
  "demo_booked",
  "brochure_sent",
  "not_interested",
];
export const CONVERSATIONS_PER_PAGE = 25;
/** Window of the best-hours map: the shortest of 30 / 90 / 365 days covering the chosen period. */
export const bestHoursDays = (from: string, to: string): CallHoursDays => {
  const n = daysInclusive(from, to);
  return n <= 30 ? 30 : n <= 90 ? 90 : 365;
};
export type RepPeriod = {
  preset: RepPeriodPreset;
  from?: string | null;
  to?: string | null;
};
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const validDate = (s: string | null | undefined): s is string =>
  !!s && ISO_DATE.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`));

/** Local dates of a period: the leaderboard's today / week / month, the last 30 days, or a custom range. */
export const repPeriodRange = (
  period: RepPeriod,
  now: Date,
): { from: string; to: string } => {
  const today = torontoDate(now);
  switch (period.preset) {
    case "today":
    case "week":
    case "month":
      return leaderboardRange(period.preset, now);
    case "last30":
      return { from: addDays(today, -29), to: today };
    case "custom":
      if (validDate(period.from) && validDate(period.to))
        return period.from <= period.to
          ? { from: period.from, to: period.to }
          : { from: period.to, to: period.from };
      return leaderboardRange("month", now);
  }
};

/** Change from the previous period; ratio is null when the previous value is 0. */
export const kpiDelta = (
  current: number | null,
  previous: number | null,
): { diff: number; ratio: number | null } | null =>
  current == null || previous == null
    ? null
    : {
        diff: round(current - previous, 4),
        ratio:
          previous !== 0 ? round((current - previous) / previous, 4) : null,
      };

export type FunnelStepKey = "dials" | "conversations" | "demos" | "deals_won";
/** Appels → Conversations → Démos → Deals gagnés, with the conversion from the previous step. */
export const funnelSteps = (k: RepKpis) => {
  const keys: FunnelStepKey[] = [
    "dials",
    "conversations",
    "demos",
    "deals_won",
  ];
  return keys.map((key, i) => ({
    key,
    value: k[key],
    conversion: i === 0 ? null : ratio(k[key], k[keys[i - 1]], 4),
  }));
};

/** Every local day of the period, zeros for days without calls. */
export const fillDays = (
  rows: readonly RepTrendDay[],
  from: string,
  to: string,
): RepTrendDay[] => {
  const byDay = new Map(rows.map((r) => [r.day, r]));
  const out: RepTrendDay[] = [];
  for (let d = from; d <= to; d = addDays(d, 1))
    out.push(byDay.get(d) ?? { day: d, dials: 0, conversations: 0, demos: 0 });
  return out;
};

/** The 8 dialer outcomes (dialer order) as count and share of logged outcomes; « non consigné » only when present. */
export const outcomeShares = (
  rows: readonly { outcome: string; count: number }[],
) => {
  const counts = new Map(rows.map((r) => [r.outcome, r.count]));
  const keys: string[] = DIAL_OUTCOMES.map((o) => o.code);
  if ((counts.get("unlogged") ?? 0) > 0) keys.push("unlogged");
  const total = keys.reduce((s, k) => s + (counts.get(k) ?? 0), 0);
  return keys.map((outcome) => {
    const count = counts.get(outcome) ?? 0;
    return { outcome, count, share: ratio(count, total, 4) };
  });
};

/** Trade / region rows with their conversation rate (conversations / dials) and demo rate (demos / conversations). */
export const segmentRates = (rows: readonly RepSegmentRow[]) =>
  rows.map((r) => ({
    ...r,
    conversation_rate: ratio(r.conversations, r.dials, 4),
    demo_rate: ratio(r.demos, r.conversations, 4),
  }));
